import { ChangeDetectionStrategy, Component, input, model } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ICategory } from '../../../core/models/catalog.model';
import { ICompany } from '../../../core/models/company.model';
import { ILogoValue } from '../logo-picker/logo-picker.component';

export function buildIdentityForm(fb: FormBuilder, c?: ICompany | null): FormGroup {
  return fb.group({
    name: [c?.name ?? '', [Validators.required, Validators.maxLength(120)]],
    legalName: [c?.legalName ?? '', [Validators.maxLength(160)]],
    idCategory: [c?.category?.idCategory ?? null, [Validators.required]],
  });
}

/** Identidad del negocio: 2 logos, nombre comercial, razón social y categoría (Agregar negocio · 1 y Ajustes · Identidad). */
@Component({
  selector: 'app-identity-fields',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="cr-field">
      <span class="cr-field__label">Logo cuadrado</span>
      <app-logo-picker purpose="logo_square" [idCompany]="idCompany()" [disabled]="disabled()" [(value)]="squareLogo" />
      <p class="cr-field__helper">Cuadrado 1:1 · 512×512 px · para el mapa y avatares.</p>
    </div>
    <div class="cr-field">
      <span class="cr-field__label">Logo horizontal</span>
      <app-logo-picker purpose="logo_horizontal" [idCompany]="idCompany()" [disabled]="disabled()" [(value)]="horizontalLogo" />
      <p class="cr-field__helper">Horizontal 3:1 · 1200×400 px · para encabezados.</p>
    </div>
    <ng-container [formGroup]="form()">
      <div class="cr-field">
        <label class="cr-field__label" for="trade-name">Nombre comercial</label>
        <input id="trade-name" class="cr-input" formControlName="name" maxlength="120" placeholder="Ej. Café Raíz" [class.is-invalid]="err('name')" />
        @if (err('name')) { <p class="cr-field__error">Escribe el nombre comercial.</p> }
        @else { <p class="cr-field__helper">Aparece en el mapa y en tus tarjetas.</p> }
      </div>
      <div class="cr-field">
        <label class="cr-field__label" for="legal-name">Razón social</label>
        <input id="legal-name" class="cr-input" formControlName="legalName" maxlength="160" placeholder="Ej. Café Raíz S.A. de C.V." />
        <p class="cr-field__helper">Como aparece en tu facturación.</p>
      </div>
      <div class="cr-field">
        <label class="cr-field__label" for="category">Categoría</label>
        <ng-select labelForId="category" formControlName="idCategory" [items]="categories()" bindValue="idCategory" bindLabel="name"
          placeholder="Elige una categoría" [clearable]="false" [class.is-invalid]="err('idCategory')">
          <ng-template ng-label-tmp let-item="item">
            <span class="cr-ng-option"><span class="cr-ng-option__icon"><span class="material-symbols-rounded">{{ item.icon }}</span></span>{{ item.name }}</span>
          </ng-template>
          <ng-template ng-option-tmp let-item="item">
            <span class="cr-ng-option"><span class="cr-ng-option__icon"><span class="material-symbols-rounded">{{ item.icon }}</span></span>{{ item.name }}</span>
          </ng-template>
        </ng-select>
        @if (err('idCategory')) { <p class="cr-field__error">Elige la categoría de tu negocio.</p> }
      </div>
    </ng-container>
  `,
  styles: [':host{display:flex;flex-direction:column;gap:28px}'],
})
export class IdentityFieldsComponent {
  readonly form = input.required<FormGroup>();
  readonly categories = input<ICategory[]>([]);
  readonly idCompany = input<string | null>(null);
  readonly showErrors = input<boolean>(false);
  /** Logos sin subir ni quitar (negocio inactivo en solo lectura); los campos se deshabilitan desde el FormGroup. */
  readonly disabled = input<boolean>(false);
  readonly squareLogo = model<ILogoValue>({ idFile: null, url: null });
  readonly horizontalLogo = model<ILogoValue>({ idFile: null, url: null });

  err(key: string): boolean {
    const c = this.form().get(key);
    return !!c && c.invalid && (this.showErrors() || c.touched);
  }
}
